import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Phone, MapPin } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import EditableCell from "../dashboard/EditableCell";

const empty = { name: "", type: "", city: "", contact: "", phone: "", email: "", notes: "", discount_pct: 0 };

export default function MobileClients() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const load = () => api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openForm = (c) => { setForm(c ? { ...c } : empty); setEditId(c?.id || null); setOpen(true); };
  const save = async () => {
    if (!form.name) { toast.error("Inserisci il nome del cliente"); return; }
    try {
      const payload = { name: form.name, type: form.type, city: form.city, contact: form.contact, phone: form.phone, email: form.email, notes: form.notes, discount_pct: Number(form.discount_pct) || 0 };
      if (editId) await api.put(`/clients/${editId}`, payload);
      else await api.post("/clients", payload);
      toast.success("Cliente salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Eliminare il cliente ${c.name}?`)) return;
    await api.delete(`/clients/${c.id}`); toast.success("Eliminato"); load();
  };

  const list = clients.filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.city || "").toLowerCase().includes(q.toLowerCase()));
  const inline = (c, field) => async (v) => {
    const payload = { name: c.name, type: c.type || "", city: c.city || "", contact: c.contact || "", phone: c.phone || "", email: c.email || "", notes: c.notes || "", discount_pct: Number(c.discount_pct) || 0, [field]: v };
    if (!payload.name) { toast.error("Il nome non può essere vuoto"); return; }
    try { await api.put(`/clients/${c.id}`, payload); toast.success("Salvato"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const cell = (c, field, extra = {}) => <EditableCell value={c[field] ?? (field === "discount_pct" ? 0 : "")} onSave={inline(c, field)} testId={`mobile-edit-client-${field}-${c.id}`} {...extra} />;

  return (
    <div data-testid="mobile-clients">
      <div className="flex items-center justify-between mb-3">
        <h1 className="font-serif text-2xl font-semibold text-[#0B2A45]">Clienti <span className="text-stone-400 text-base">({clients.length})</span></h1>
        <Button onClick={() => openForm(null)} data-testid="mobile-add-client-btn" className="bg-[#0F4C81] text-white rounded-full h-10 px-4"><Plus className="w-4 h-4 mr-1" />Nuovo</Button>
      </div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome o città…" className="h-11 mb-1" data-testid="mobile-clients-search" />
      <p className="text-[11px] text-stone-400 mb-4">Tocca un dato per modificarlo: si salva subito.</p>

      <div className="space-y-3">
        {list.length === 0 && <p className="text-center text-stone-400 py-10">Nessun cliente</p>}
        {list.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border border-stone-200 p-4" data-testid={`mobile-client-${c.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[#0B2A45]">{cell(c, "name")}</div>
                <div className="text-xs text-stone-500 grid grid-cols-2 gap-1">
                  <div><span className="text-[10px] uppercase text-stone-400">Tipo</span>{cell(c, "type", { placeholder: "Bar, Lido…" })}</div>
                  <div><span className="text-[10px] uppercase text-stone-400 inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />Città</span>{cell(c, "city")}</div>
                  <div><span className="text-[10px] uppercase text-stone-400">Referente</span>{cell(c, "contact")}</div>
                  <div><span className="text-[10px] uppercase text-stone-400">Tel.</span>{cell(c, "phone", { type: "tel" })}</div>
                  <div className="col-span-2"><span className="text-[10px] uppercase text-stone-400">Email</span>{cell(c, "email", { type: "email" })}</div>
                </div>
              </div>
              <div className="shrink-0 w-20 text-right">
                <div className="text-xs font-semibold text-[#854D0E] flex items-center justify-end">-{cell(c, "discount_pct", { type: "number", className: "text-right" })}%</div>
                <div className="text-[10px] uppercase tracking-wide text-stone-400">sconto</div>
              </div>
            </div>
            <div className="flex gap-2 mt-3 pt-3 border-t border-stone-100">
              {c.phone && <a href={`tel:${c.phone}`} className="flex-1 h-10 rounded-xl bg-stone-50 flex items-center justify-center text-stone-600" data-testid={`mobile-call-client-${c.id}`}><Phone className="w-4 h-4" /></a>}
              <button onClick={() => openForm(c)} className="flex-1 h-10 rounded-xl bg-stone-50 flex items-center justify-center text-[#0F4C81]" data-testid={`mobile-edit-client-${c.id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(c)} className="flex-1 h-10 rounded-xl bg-red-50 flex items-center justify-center text-red-600" data-testid={`mobile-delete-client-${c.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg w-[calc(100%-2rem)] rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Modifica" : "Nuovo"} cliente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome / Insegna *</Label><Input data-testid="mobile-client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-11" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tipo</Label><Input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="Bar, Lido…" className="mt-1 h-11" /></div>
              <div><Label>Città</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 h-11" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Referente</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="mt-1 h-11" /></div>
              <div><Label>Telefono</Label><Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11" /></div>
            </div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-11" /></div>
            <div><Label>Sconto concordato %</Label><Input type="number" step="0.5" inputMode="decimal" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} className="mt-1 h-11" /></div>
            <div><Label>Note</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="mobile-save-client-btn" className="bg-[#0F4C81] text-white rounded-full h-11 w-full">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
